import React from "react";
import {
  LoadingSectionBody,
  LoadingGridLayout,
  LoadingContentStackTertiary,
  LoadingSectionQuaternary,
  LoadingGridLayoutSecondary,
  LoadingInfoRow,
  LoadingContentStackQuaternary,
  LoadingSectionQuinary,
  LoadingPanelCardSecondary,
  LoadingScrollArea,
  LoadingContentStack,
  LoadingContentStackQuinary,
  LoadingSectionSenary,
  LoadingContentStackSenary,
  LoadingGridLayoutTertiary,
  LoadingSectionSeptenary,
  LoadingSection,
  LoadingContentStackSecondary,
  LoadingSectionBodySecondary,
  LoadingPanelCard,
  LoadingSectionSecondary,
  LoadingMediaSurface,
  LoadingSectionTertiary,
  LoadingDataTable,
  LoadingHeaderCell,
  PageHeaderSkeletonSectionBody,
  PageHeaderSkeletonTextStack,
} from "components/admin/styles/loading.inline.styles";
export const SkeletonBlock: React.FC<{
  className?: string;
}> = ({ className = "" }) => (
  <div
    aria-hidden="true"
    className={`brand-skeleton rounded-[18px] ${className}`.trim()}
    data-testid="skeleton-block"
  />
);
export const PageHeaderSkeleton: React.FC<{
  actionCount?: number;
  descriptionWidthClassName?: string;
  titleWidthClassName?: string;
  withActions?: boolean;
}> = ({
  actionCount = 2,
  descriptionWidthClassName = "w-full max-w-2xl",
  titleWidthClassName = "w-52",
  withActions = true,
}) => {
  const headerContent = (
    <LoadingContentStack>
      <PageHeaderSkeletonTextStack>
        <SkeletonBlock
          className={`h-9 shrink-0 self-start rounded-[18px] sm:h-10 ${titleWidthClassName}`.trim()}
        />
        <SkeletonBlock
          className={`h-3 shrink-0 self-start rounded-full ${descriptionWidthClassName}`.trim()}
        />
      </PageHeaderSkeletonTextStack>
    </LoadingContentStack>
  );

  if (!withActions) {
    return (
      <PageHeaderSkeletonSectionBody aria-label="Loading page header" role="status">
        {headerContent}
      </PageHeaderSkeletonSectionBody>
    );
  }

  return (
    <LoadingSectionBody aria-label="Loading page header" role="status">
      {headerContent}
      <LoadingSection>
        {Array.from({
          length: actionCount,
        }).map((_, index) => (
          <SkeletonBlock className="h-11 w-28 shrink-0 rounded-2xl" key={index} />
        ))}
      </LoadingSection>
    </LoadingSectionBody>
  );
};
export const SectionCardSkeleton: React.FC<{
  children?: React.ReactNode;
  subtitleWidthClassName?: string;
  titleWidthClassName?: string;
  withAction?: boolean;
}> = ({
  children,
  subtitleWidthClassName = "w-28",
  titleWidthClassName = "w-44",
  withAction = false,
}) => (
  <LoadingContentStackSecondary aria-label="Loading section" role="status">
    <LoadingSectionBodySecondary>
      <LoadingContentStack>
        <SkeletonBlock
          className={`h-3 shrink-0 self-start rounded-full ${subtitleWidthClassName}`.trim()}
        />
        <SkeletonBlock
          className={`h-8 shrink-0 self-start rounded-[18px] ${titleWidthClassName}`.trim()}
        />
      </LoadingContentStack>

      {withAction ? <SkeletonBlock className="h-10 w-24 rounded-2xl" /> : null}
    </LoadingSectionBodySecondary>

    {children}
  </LoadingContentStackSecondary>
);
export const CoverageMapSkeleton: React.FC<{
  heightClassName?: string;
}> = ({ heightClassName = "h-[420px]" }) => (
  <LoadingPanelCard aria-label="Loading coverage map" role="status">
    <div
      className={`relative overflow-hidden rounded-[22px] border border-[var(--brand-border)] bg-[rgba(255,255,255,0.04)] ${heightClassName}`.trim()}
    >
      <LoadingSectionSecondary />
      <SkeletonBlock className="absolute left-[14%] top-[28%] h-5 w-5 rounded-full" />
      <SkeletonBlock className="absolute left-[44%] top-[38%] h-4 w-4 rounded-full" />
      <SkeletonBlock className="absolute left-[62%] top-[58%] h-6 w-6 rounded-full" />
      <SkeletonBlock className="absolute left-[76%] top-[22%] h-4 w-4 rounded-full" />
      <SkeletonBlock className="absolute bottom-6 left-6 h-4 w-40 rounded-full" />
    </div>
  </LoadingPanelCard>
);
export const StatCardSkeleton: React.FC = () => (
  <LoadingMediaSurface data-testid="stat-card-skeleton">
    <LoadingSectionTertiary />
    <SkeletonBlock className="h-3 w-24 rounded-full" />
    <SkeletonBlock className="mt-4 h-10 w-28 rounded-[20px]" />
    <SkeletonBlock className="mt-4 h-4 w-40 rounded-full" />
  </LoadingMediaSurface>
);
export const StatsGridSkeleton: React.FC<{
  count?: number;
}> = ({ count = 4 }) => (
  <LoadingGridLayout>
    {Array.from({
      length: count,
    }).map((_, index) => (
      <StatCardSkeleton key={index} />
    ))}
  </LoadingGridLayout>
);
export const DataTableSkeleton: React.FC<{
  columns?: number;
  rows?: number;
  showThumbnail?: boolean;
  showActions?: boolean;
}> = ({ columns = 4, rows = 5, showThumbnail = true, showActions = true }) => (
  <LoadingContentStack aria-label="Loading table content" role="status">
    <LoadingContentStackTertiary>
      {Array.from({
        length: Math.min(rows, 3),
      }).map((_, index) => (
        <LoadingSectionQuaternary key={`mobile-${index}`}>
          <LoadingContentStack>
            {Array.from({
              length: columns,
            }).map((__, columnIndex) => (
              <LoadingGridLayoutSecondary key={columnIndex}>
                <SkeletonBlock className="h-3 w-16 rounded-full" />
                <LoadingInfoRow>
                  {showThumbnail && columnIndex === 0 ? (
                    <SkeletonBlock className="h-12 w-12 shrink-0 rounded-2xl" />
                  ) : null}
                  <LoadingContentStackQuaternary>
                    <SkeletonBlock className="h-4 w-2/3 rounded-full" />
                    <SkeletonBlock className="h-3 w-1/2 rounded-full" />
                  </LoadingContentStackQuaternary>
                </LoadingInfoRow>
              </LoadingGridLayoutSecondary>
            ))}
            {showActions ? (
              <LoadingSectionQuinary>
                <SkeletonBlock className="h-9 w-20 rounded-xl" />
                <SkeletonBlock className="h-9 w-20 rounded-xl" />
              </LoadingSectionQuinary>
            ) : null}
          </LoadingContentStack>
        </LoadingSectionQuaternary>
      ))}
    </LoadingContentStackTertiary>

    <LoadingPanelCardSecondary>
      <LoadingScrollArea>
        <LoadingDataTable>
          <thead>
            <tr>
              {Array.from({
                length: columns,
              }).map((_, index) => (
                <th key={index}>
                  <SkeletonBlock className="h-3 w-16 rounded-full" />
                </th>
              ))}
              {showActions ? (
                <LoadingHeaderCell>
                  <SkeletonBlock className="h-3 w-16 rounded-full" />
                </LoadingHeaderCell>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {Array.from({
              length: rows,
            }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                {Array.from({
                  length: columns,
                }).map((__, columnIndex) => (
                  <td key={columnIndex}>
                    <LoadingInfoRow>
                      {showThumbnail && columnIndex === 0 ? (
                        <SkeletonBlock className="h-12 w-12 shrink-0 rounded-2xl" />
                      ) : null}
                      <LoadingContentStackQuinary>
                        <SkeletonBlock className="h-4 w-2/3 rounded-full" />
                        <SkeletonBlock className="h-3 w-1/2 rounded-full" />
                      </LoadingContentStackQuinary>
                    </LoadingInfoRow>
                  </td>
                ))}
                {showActions ? (
                  <td>
                    <LoadingSectionSenary>
                      <SkeletonBlock className="h-9 w-9 rounded-xl" />
                      <SkeletonBlock className="h-9 w-9 rounded-xl" />
                    </LoadingSectionSenary>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </LoadingDataTable>
      </LoadingScrollArea>
    </LoadingPanelCardSecondary>
  </LoadingContentStack>
);
export const DetailSectionSkeleton: React.FC<{
  imageClassName?: string;
  imageHeightClassName?: string;
  infoCardCount?: number;
}> = ({ imageClassName, imageHeightClassName = "h-80", infoCardCount = 4 }) => (
  <LoadingContentStackSenary aria-label="Loading detail section" role="status">
    <SkeletonBlock
      className={`${imageClassName || `${imageHeightClassName} w-full`} rounded-[24px]`.trim()}
    />
    <LoadingGridLayoutTertiary>
      <LoadingSectionSeptenary>
        <SkeletonBlock className="h-3 w-20 rounded-full" />
        <SkeletonBlock className="mt-4 h-4 w-full rounded-full" />
        <SkeletonBlock className="mt-3 h-4 w-5/6 rounded-full" />
        <SkeletonBlock className="mt-3 h-4 w-2/3 rounded-full" />
      </LoadingSectionSeptenary>
      {Array.from({
        length: infoCardCount,
      }).map((_, index) => (
        <LoadingSectionQuaternary key={index}>
          <SkeletonBlock className="h-3 w-16 rounded-full" />
          <SkeletonBlock className="mt-4 h-6 w-24 rounded-full" />
        </LoadingSectionQuaternary>
      ))}
    </LoadingGridLayoutTertiary>
  </LoadingContentStackSenary>
);

